import { memo, useState, useEffect, useRef, useCallback } from 'react';
import { Send, Square, Loader2, Copy, Check, X, Plus, Calendar, BookOpen, Bus, Clock } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useUserPreferences } from '@/store/useStore';
import type { AssistantAttendanceSnapshot, AssistantConversationIdentity, AssistantMessage as AssistantMessageType, AssistantResponse, ConversationMessage } from '@/types/assistant';
import { OwletResponseContent } from '@/components/assistant/OwletResponse';
import { requestAssistant } from '@/services/assistantService';
import { attendanceStorage } from '@/services/attendanceStorage';

// ============ Design tokens (mirror the dark dashboard) ============

const T = {
  bg:         '#0A0A0A',
  surface:    'rgba(255,255,255,.03)',
  surfaceHov: 'rgba(255,255,255,.055)',
  border:     'rgba(255,255,255,.07)',
  borderHov:  'rgba(255,255,255,.13)',
  teal:       '#14B8A6',
  tealBg:     'rgba(20,184,166,.1)',
  tealBorder: 'rgba(20,184,166,.22)',
  text:       '#fff',
  text50:     'rgba(255,255,255,.5)',
  text35:     'rgba(255,255,255,.35)',
  text25:     'rgba(255,255,255,.25)',
  mono:       "'JetBrains Mono', monospace",
  serif:      "'Instrument Serif', serif",
  sans:       "'Outfit', sans-serif",
};

const MAX_STORED_MESSAGES = 60;
const MAX_CONTEXT_MESSAGES = 18;
const MAX_CONTEXT_CHARS = 32_000;

// ============ Message Bubble Component ============

function WaitingMessage({ label }: { label?: string }) {
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaiting(true), 8000);
    return () => clearTimeout(timer);
  }, []);
  return <span>{label || (waiting ? 'Still waiting for Owlet…' : 'Working on it…')}</span>;
}

const MessageBubble = memo(function MessageBubble({ message }: { message: AssistantMessageType }) {
  const isUser = message.role === 'user';
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (message.isLoading && !message.content) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 22 }}>
        <img src="/logo2.png" alt="Owlet" style={{ width: 28, height: 28, objectFit: 'contain', marginTop: 2, flexShrink: 0 }} />
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text50, fontFamily: T.sans, fontSize: 13, marginTop: 4 }}>
          <Loader2 className="animate-spin" size={15} color={T.teal} />
          <WaitingMessage label={message.activityLabel} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="owlet-msg"
      style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 32, flexDirection: isUser ? 'row-reverse' : 'row' }}
    >
      {/* Avatar */}
      {!isUser && (
        <img src="/logo2.png" alt="Owlet" style={{ width: 28, height: 28, objectFit: 'contain', marginTop: 2, flexShrink: 0 }} />
      )}

      {/* Message Content */}
      <div className="owlet-message-content" style={{
        flex: 1, display: 'flex',
        flexDirection: isUser ? 'row' : 'column',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        alignItems: isUser ? 'center' : 'stretch',
        gap: isUser ? 10 : 0,
      }}>
        {/* User copy action (on hover) */}
        {isUser && (
          <button onClick={handleCopy} className="owlet-icon-btn owlet-copy" style={iconBtnStyle} aria-label={isCopied ? 'Message copied' : 'Copy your message'} title={isCopied ? 'Copied' : 'Copy message'}>
            {isCopied ? <Check size={15} color={T.teal} /> : <Copy size={15} />}
          </button>
        )}

        {/* Bubble */}
        <div style={isUser ? {
          display: 'inline-block', maxWidth: '85%',
          background: 'rgba(20,184,166,.13)', border: `1px solid ${T.tealBorder}`,
          color: '#fff', borderRadius: 16, padding: '10px 16px',
        } : {
          width: '100%', color: 'rgba(255,255,255,.92)',
        }}>
          <div aria-busy={message.isStreaming || undefined}>
            <OwletResponseContent content={message.content} role={message.role} />
          </div>
          {message.isStreaming && (
            <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 12, color: T.text50, fontSize: 12 }}>
              <Loader2 size={12} className="animate-spin" /> Writing…
            </span>
          )}
          {message.interrupted && (
            <p role="status" style={{ color: T.text50, marginTop: 10, fontSize: 12 }}>
              {message.interrupted === 'stopped' ? 'Response stopped.' : 'Response interrupted. Please try again.'}
            </p>
          )}

          {/* Bot copy action */}
          {!isUser && !message.isStreaming && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, maxWidth: '76ch' }}>
              <button onClick={handleCopy} className="owlet-icon-btn" style={iconBtnStyle} aria-label={isCopied ? 'Response copied' : 'Copy response'} title={isCopied ? 'Copied' : 'Copy response'}>
                {isCopied ? <Check size={15} color={T.teal} /> : <Copy size={15} />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const iconBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: T.text35, transition: '.15s',
};

// ============ Welcome Screen ============

function WelcomeScreen({ onSelect, disabled }: { onSelect: (query: string) => void; disabled: boolean }) {
  const actions = [
    { label: "Today's classes", query: 'What classes do I have today?', icon: Calendar },
    { label: 'Next class', query: 'When is my next class?', icon: Clock },
    { label: 'Bus routes', query: 'Show me the bus routes', icon: Bus },
    { label: 'Exam dates', query: 'When are my final exams?', icon: BookOpen },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '0 24px' }}>
      {/* Welcome message */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 32, textAlign: 'center' }}>
        <span style={{ fontFamily: T.serif, fontSize: 22, color: '#fff', fontWeight: 400 }}>I'm all eyes.</span>
        <img src="/logo2.png" alt="Owlet" style={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }} />
        <span style={{ fontFamily: T.serif, fontSize: 22, color: '#fff', fontWeight: 400 }}>What are we working on?</span>
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, maxWidth: 380 }}>
        {actions.map((action, i) => (
          <button
            key={i}
            onClick={() => onSelect(action.query)}
            disabled={disabled}
            className="owlet-chip"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 15px', borderRadius: 999,
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.text50, fontFamily: T.sans, fontSize: 13,
              cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, transition: '.15s',
            }}
          >
            <action.icon size={15} color={T.teal} />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ Constants ============

const CHAT_STORAGE_KEY = 'owlet_chat_history';
const CHAT_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
const ATTENDANCE_SHARING_KEY = 'owlet_attendance_sharing';

interface StoredChatState {
  messages?: AssistantMessageType[];
  timestamp?: number;
  profileKey?: string;
  conversation?: AssistantConversationIdentity;
}

function conversationProfileKey(profile: ReturnType<typeof useUserPreferences>): string {
  if (!profile) return '';
  return [profile.program, profile.semester, profile.group, profile.year]
    .map(value => String(value || '').trim().toLowerCase()).join('|');
}

function isStoredConversation(value: unknown): value is AssistantConversationIdentity {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string' && /^[0-9a-f-]{36}$/i.test(candidate.id) &&
    typeof candidate.token === 'string' && /^[0-9a-f]{64}$/i.test(candidate.token);
}

function createConversationIdentity(): AssistantConversationIdentity {
  const tokenBytes = crypto.getRandomValues(new Uint8Array(32));
  return {
    id: crypto.randomUUID(),
    token: Array.from(tokenBytes, byte => byte.toString(16).padStart(2, '0')).join(''),
  };
}

// ============ Main Component ============

export default function ChatAssistant({ onClose }: { onClose?: () => void }) {
  const [messages, setMessages] = useState<AssistantMessageType[]>([]);
  const [input, setInput] = useState('');
  const [attendanceAvailable, setAttendanceAvailable] = useState(false);
  const [shareAttendance, setShareAttendance] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const followResponse = useRef(true);
  const requestController = useRef<AbortController | null>(null);
  const pendingId = useRef('');
  const conversationRef = useRef<AssistantConversationIdentity | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();
  useEffect(() => () => requestController.current?.abort('unmounted'), []);

  // Get user preferences from the app's store (set during initial signup)
  const userPreferences = useUserPreferences();
  const profileKey = conversationProfileKey(userPreferences);

  // Attendance remains local unless the student explicitly enables Owlet access.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const consent = await attendanceStorage.getConsent();
        const attendance = consent ? await attendanceStorage.getAttendance() : null;
        if (!active) return;
        setAttendanceAvailable(Boolean(attendance?.courses?.length));
        setShareAttendance(Boolean(attendance?.courses?.length) && localStorage.getItem(ATTENDANCE_SHARING_KEY) === 'true');
      } catch {
        if (active) setAttendanceAvailable(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Restore the active thread and its local transcript. The opaque token never appears in the UI.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      if (stored) {
        const saved = JSON.parse(stored) as StoredChatState;
        const { messages: savedMessages, timestamp } = saved;
        const now = Date.now();

        if (typeof timestamp === 'number' && now - timestamp < CHAT_EXPIRY_MS &&
            Array.isArray(savedMessages) && (!saved.profileKey || saved.profileKey === profileKey)) {
          conversationRef.current = isStoredConversation(saved.conversation)
            ? saved.conversation : createConversationIdentity();
          const restoredMessages = savedMessages.filter((m): m is AssistantMessageType =>
            m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string' && !m.isLoading && !m.isStreaming
          ).slice(-MAX_STORED_MESSAGES).map((m: AssistantMessageType) => ({
            ...m,
            timestamp: new Date(m.timestamp)
          }));
          setMessages(restoredMessages);
          localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify({
            messages: restoredMessages,
            timestamp: now,
            profileKey,
            conversation: conversationRef.current,
          }));
          return;
        }
      }
      conversationRef.current = createConversationIdentity();
      setMessages([]);
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify({
        messages: [], timestamp: Date.now(), profileKey, conversation: conversationRef.current,
      }));
    } catch (e) {
      console.error('Failed to load chat history:', e);
      conversationRef.current = createConversationIdentity();
    }
  }, [profileKey]);

  // Save chat history to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0 && !messages.some(m => m.isLoading || m.isStreaming)) {
      const nonLoadingMessages = messages.filter(m => !m.interrupted);
      if (nonLoadingMessages.length > 0) {
        try {
          localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify({
            messages: nonLoadingMessages,
            timestamp: Date.now(),
            profileKey,
            conversation: conversationRef.current,
          }));
        } catch (e) {
          console.error('Failed to save chat history:', e);
        }
      }
    }
  }, [messages, profileKey]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Follow live text only while the reader is already near the bottom.
  useEffect(() => {
    const container = scrollRef.current;
    if (container && followResponse.current) container.scrollTop = container.scrollHeight;
  }, [messages]);

  // Get conversation history for context
  const getConversationHistory = useCallback((): ConversationMessage[] => {
    const recent = messages
      .filter(m => !m.isLoading && !m.isStreaming && !m.interrupted && m.id !== 'welcome')
      .slice(-MAX_CONTEXT_MESSAGES)
      .map(m => ({ role: m.role, content: m.content }));
    let size = 0;
    return recent.reverse().filter(m => {
      size += m.content.length;
      return m.content.length <= 8000 && size <= MAX_CONTEXT_CHARS;
    }).reverse();
  }, [messages]);

  const chatMutation = useMutation({
    mutationFn: async (message: string): Promise<AssistantResponse> => {
      if (!userPreferences) throw new Error('Set your program, semester and group in your profile first.');
      const studentProfile = {
        program: userPreferences.program,
        semester: userPreferences.semester,
        group: userPreferences.group,
        year: userPreferences.year
      };

      let attendanceSnapshot: AssistantAttendanceSnapshot | undefined;
      if (shareAttendance && attendanceAvailable) {
        const attendance = await attendanceStorage.getAttendance().catch(() => null);
        if (attendance?.courses?.length) {
          attendanceSnapshot = {
            fetchedAt: new Date(attendance.fetchedAt).toISOString(),
            ...(attendance.semester ? { semester: attendance.semester } : {}),
            ...(attendance.session ? { session: attendance.session } : {}),
            courses: attendance.courses.slice(0, 100).map(course => ({
              courseName: course.courseName,
              ...(course.courseCode ? { courseCode: course.courseCode } : {}),
              totalClasses: course.totalClasses,
              presents: course.presents,
              absents: course.absents,
              attendancePercentage: course.attendancePercentage,
            })),
          };
        }
      }

      const requestBody = {
        requestId: pendingId.current,
        message,
        studentProfile,
        conversation: conversationRef.current || createConversationIdentity(),
        conversationHistory: getConversationHistory(),
        ...(attendanceSnapshot ? { attendanceSnapshot } : {}),
      };
      conversationRef.current = requestBody.conversation;

      const controller = new AbortController();
      requestController.current = controller;
      const deadline = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), 90_000);
      const messageId = pendingId.current;
      let latestText = '';
      let lastPaint = 0;
      let paintTimer: ReturnType<typeof setTimeout> | undefined;
      const paint = () => {
        paintTimer = undefined;
        lastPaint = Date.now();
        setMessages(prev => prev.map(m => m.id === messageId
          ? { ...m, content: latestText, isLoading: false, isStreaming: true, activityLabel: undefined } : m));
      };
      try {
        return await requestAssistant(requestBody, text => {
          latestText = text;
          if (Date.now() - lastPaint >= 24) {
            clearTimeout(paintTimer);
            paint();
          } else if (!paintTimer) paintTimer = setTimeout(paint, 24);
        }, controller.signal, label => {
          if (!latestText) setMessages(prev => prev.map(m => m.id === messageId ? { ...m, activityLabel: label } : m));
        });
      } finally {
        clearTimeout(deadline);
        clearTimeout(paintTimer);
        if (latestText && controller.signal.reason !== 'unmounted') paint();
      }
    },
    onSuccess: (data) => {
      setMessages(prev => prev.map(m => m.id === pendingId.current ? {
          ...m,
          content: data.answer,
          sources: data.sources,
          contextType: data.contextType,
          generation: data.generation,
          isLoading: false, isStreaming: false, activityLabel: undefined,
        } : m));
    },
    onError: (error) => {
      if (requestController.current?.signal.reason === 'unmounted') return;
      const stopped = requestController.current?.signal.reason === 'stopped';
      const errorMessage = error instanceof Error && error.name !== 'TimeoutError'
        ? error.message : 'Owlet took too long to respond. Please try again.';
      setMessages(prev => prev.map(m => {
        if (m.id !== pendingId.current) return m;
        const hasPartialResponse = Boolean(m.content.trim());
        return {
          ...m,
          content: hasPartialResponse ? m.content : (stopped ? '' : errorMessage),
          isLoading: false,
          isStreaming: false,
          activityLabel: undefined,
          interrupted: stopped ? 'stopped' : (hasPartialResponse ? 'error' : undefined),
        };
      }));
      if (!stopped) toast({ title: 'Owlet could not finish', description: errorMessage, variant: 'destructive' });
    },
    onSettled: () => { requestController.current = null; },
  });

  const handleSend = useCallback((messageText?: string) => {
    const text = messageText || input;
    if (!text.trim() || chatMutation.isPending) return;
    pendingId.current = crypto.randomUUID();
    followResponse.current = true;

    const userMessage: AssistantMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    const loadingMessage: AssistantMessageType = {
      id: pendingId.current,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true
    };

    setMessages(prev => {
      const trimmed = prev.length >= MAX_STORED_MESSAGES ? prev.slice(-MAX_STORED_MESSAGES + 2) : prev;
      return [...trimmed, userMessage, loadingMessage];
    });

    chatMutation.mutate(text);
    setInput('');
  }, [input, chatMutation]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = useCallback(() => {
    requestController.current?.abort('stopped');
    pendingId.current = '';
    followResponse.current = true;
    conversationRef.current = createConversationIdentity();
    setMessages([]);
    setInput('');
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify({
        messages: [], timestamp: Date.now(), profileKey, conversation: conversationRef.current,
      }));
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    }
    queueMicrotask(() => textareaRef.current?.focus());
  }, [profileKey]);

  const showWelcomeScreen = messages.length === 0 && !chatMutation.isPending;

  return (
    <div className="owlet-chat" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg, fontFamily: T.sans, color: T.text }}>
      <style>{`
        .owlet-icon-btn:hover { color: #fff !important; background: rgba(255,255,255,.06) !important; }
        .owlet-copy { opacity: 0; transition: opacity .2s; }
        .owlet-msg:hover .owlet-copy { opacity: 1; }
        .owlet-chip:hover:not(:disabled) { background: rgba(20,184,166,.08) !important; border-color: rgba(20,184,166,.25) !important; color: rgba(255,255,255,.8) !important; }
        .owlet-input:focus-within { border-color: rgba(20,184,166,.4) !important; box-shadow: 0 0 0 3px rgba(20,184,166,.08); }
        .owlet-textarea::placeholder { color: rgba(255,255,255,.3); }
        .owlet-send:hover:not(:disabled) { background: #10a596 !important; }
        .owlet-new-chat:hover { color: #fff !important; }
      `}</style>

      {/* Header */}
      <header style={{
        flexShrink: 0, padding: '13px 16px', borderBottom: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, lineHeight: 1, color: '#fff' }}>
          Owlet
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={handleNewChat}
            className="owlet-new-chat"
            aria-label="Start a new chat"
            title="Start a new chat"
            style={{
              width: 34, height: 34, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 0, background: 'transparent', color: T.text50, cursor: 'pointer', transition: 'color .15s',
            }}
          >
            <Plus size={20} />
          </button>
          {onClose && (
            <button onClick={onClose} className="owlet-icon-btn" style={{ ...iconBtnStyle, width: 34, height: 34, borderRadius: 10 }} aria-label="Close">
              <X size={18} />
            </button>
          )}
        </div>
      </header>

      {/* Messages or Welcome Screen */}
      {showWelcomeScreen ? (
        <WelcomeScreen onSelect={handleSend} disabled={chatMutation.isPending} />
      ) : (
        <div ref={scrollRef} role="log" aria-label="Conversation with Owlet" aria-live="off"
          onScroll={event => {
            const box = event.currentTarget;
            followResponse.current = box.scrollHeight - box.scrollTop - box.clientHeight < 96;
          }}
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '28px 16px' }}>
          <div style={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
            {messages.map((message) => <MessageBubble key={message.id} message={message} />)}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div style={{ flexShrink: 0, padding: 16, width: '100%', maxWidth: 832, margin: '0 auto' }}>
        {chatMutation.isPending && <span className="sr-only" role="status">Owlet is responding.</span>}
        <div className="owlet-input" style={{
          position: 'relative', background: T.surface,
          borderRadius: 16, border: `1px solid ${T.border}`, transition: 'border-color .2s, box-shadow .2s',
        }}>
          <textarea
            ref={textareaRef}
            aria-label="Message Owlet"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Owlet…"
            maxLength={4000}
            rows={1}
            className="owlet-textarea"
            style={{
              width: '100%', resize: 'none', background: 'transparent', color: '#fff',
              outline: 'none', border: 'none', fontFamily: T.sans,
              padding: '15px 56px 15px 18px', fontSize: 14.5, lineHeight: 1.5,
              minHeight: 54, maxHeight: 120, overflowY: 'auto',
            }}
          />
          <button
            onClick={() => chatMutation.isPending ? requestController.current?.abort('stopped') : handleSend()}
            disabled={!chatMutation.isPending && !input.trim()}
            className="owlet-send"
            aria-label={chatMutation.isPending ? 'Stop response' : 'Send message'}
            title={chatMutation.isPending ? 'Stop response' : 'Send message'}
            style={{
              position: 'absolute', bottom: 9, right: 9,
              width: 36, height: 36, borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: !chatMutation.isPending && !input.trim() ? 'rgba(20,184,166,.25)' : T.teal,
              color: !chatMutation.isPending && !input.trim() ? 'rgba(255,255,255,.5)' : '#06231f',
              border: 'none', cursor: !chatMutation.isPending && !input.trim() ? 'not-allowed' : 'pointer',
              transition: '.15s',
            }}
          >
            {chatMutation.isPending ? <Square size={14} fill="currentColor" /> : <Send size={17} />}
          </button>
        </div>
        <p style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.08em', textAlign: 'center', color: T.text25, marginTop: 10 }}>
          OWLET MAY MAKE MISTAKES · VERIFY IMPORTANT INFO
        </p>
        {attendanceAvailable && (
          <button
            type="button"
            role="switch"
            aria-checked={shareAttendance}
            onClick={() => {
              const next = !shareAttendance;
              setShareAttendance(next);
              localStorage.setItem(ATTENDANCE_SHARING_KEY, String(next));
            }}
            style={{
              display: 'block', margin: '7px auto 0', padding: 0, border: 0, background: 'transparent',
              color: shareAttendance ? T.teal : T.text35, fontFamily: T.sans, fontSize: 11, cursor: 'pointer',
            }}
            title="Allow Owlet to use your local attendance records"
          >
            {shareAttendance ? 'Attendance access on' : 'Allow Owlet to use local attendance'}
          </button>
        )}
      </div>
    </div>
  );
}
